import React, { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROUTER_COOKIES, ROUTER_PERSONAL_DATA, ROUTER_TERMS_AND_CONDITIONS } from 'app/AppRouter';
import { InfoIcon } from 'assets/icons';
import { InfoDialog, useDisclosure } from 'components/Dialog';
import { getOilVolumeLiters, isNonBulkCertasOil } from 'components/StockInfo/catalogStockUtils';
import { useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import { ExternalBasketSectionLocal, ReferenceLocal, VehicleLocal } from 'domains/basket/Basket.types';
import { getSelectedItemsCount } from 'domains/basket/Basket.utils';
import { getUserCountry } from 'domains/user';
import { Box, CenteredSpin, Flex, Link, SPACE_BETWEEN, Text, YellowButton } from 'UI';
import { textFormatter } from 'utils/format';
import { getBrowserLanguage } from 'utils/i18n/localeDetector';
import { Card, CheckoutCardWrapper, Divider } from './CheckoutCardSection.styled';

const MINIMUM_CHECKOUT_OIL_QUANTITY = 200;

const LinkText = ({ children, linkTo }: { children?: ReactNode; linkTo: string }) => (
  <Link to={linkTo} external newTab>
    <Text type="base" color="grey35" decoration={'underline'}>
      {children}
    </Text>
  </Link>
);

interface CheckoutCardProps {
  totalPriceVatIncluded: number | string;
  totalPriceVatExcluded: number | string;
  totalDiscount: number;
  totalVat: number | string;
  vehicles: VehicleLocal[];
  externalBaskets: ExternalBasketSectionLocal[];
  otherReferences?: ReferenceLocal[];
  currency: string;
  onCheckout: () => void;
  loading?: boolean;
  disabledCheckout?: boolean;
  validationStep?: boolean;
  estimateTotalReferences?: number;
}

const CheckoutCardSection = ({
  totalPriceVatIncluded,
  totalPriceVatExcluded,
  totalVat,
  vehicles,
  externalBaskets,
  otherReferences,
  currency,
  onCheckout,
  loading = false,
  disabledCheckout = false,
  validationStep,
  totalDiscount,
  estimateTotalReferences,
}: CheckoutCardProps) => {
  const { t } = useTranslation();
  const disclosure = useDisclosure();

  const userCountry = useSelector(getUserCountry);
  const browserCountry = getBrowserLanguage()?.getCountryCode();
  const country = userCountry?.toLowerCase() ?? browserCountry?.toLowerCase();

  const { onOpen, onClose } = disclosure;
  const oilReferences =
    otherReferences?.filter((ref) => isNonBulkCertasOil(ref.familyCode, ref.productModelCode, ref.packaging)) ?? [];

  const itemsCount = getSelectedItemsCount(vehicles, externalBaskets, otherReferences ?? []);

  const isOilUser = useIsOilChunkUserR1();

  const isCheckoutForOilsAllowed = () => {
    if (!isOilUser || oilReferences.length === 0) {
      return true;
    }

    const oilRefQtyTotal =
      oilReferences.reduce((total, ref) => {
        const volume = getOilVolumeLiters(ref);
        const literPerRef = volume || (volume > 0 ? volume : 1);
        return total + ref.quantity * literPerRef;
      }, 0) ?? 0;

    return oilRefQtyTotal >= MINIMUM_CHECKOUT_OIL_QUANTITY;
  };

  const handleCheckOut = () => {
    if (isCheckoutForOilsAllowed()) {
      onCheckout();
    } else {
      onOpen();
    }
  };

  return (
    <CheckoutCardWrapper data-cy={'cart-checkout-summary'}>
      <InfoDialog
        title={'Action required'}
        handleConfirm={() => undefined}
        disclosure={disclosure}
        icon={InfoIcon}
        data-cy={'minimum-oil-quantity-dialog'}
        description={[
          t(
            'catalog.universal_products.basket.minimum_quantity.notification.first_line',
            'Minimum order quantity of 200L is not reached for Castrol Oil reference(s).',
          ),
          t(
            'catalog.universal_products.basket.minimum_quantity.notification.second_line',
            'Please adjust quantities and try again.',
          ),
        ]}
        content={
          <Flex minWidth={400} direction={'row-reverse'}>
            <YellowButton onClick={onClose} data-cy={'confirm-button'}>
              <Flex minWidth={80} justify={'center'}>
                {t('common.action.confirm', 'Confirm')}
              </Flex>
            </YellowButton>
          </Flex>
        }
      />
      <Card>
        <Flex
          direction={'row'}
          justify={SPACE_BETWEEN}
          align={'center'}
          padding={'19px 24px 19px 24px'}
          data-cy={'total-order'}
          wrap={['wrap']}
        >
          <Text type={'h1_black'} disableGutter>
            {t('common.price.total_order', 'Total order')}
          </Text>
          <Text type="base" color="grey55" data-cy={'total-order-items-count'}>
            {t('catalog.checkout_card.total.item2', '{{count}} items', {
              count: estimateTotalReferences ?? itemsCount,
            })}
          </Text>
        </Flex>
        <Divider />
        <Flex direction={'column'} gap={16} padding={24}>
          <Flex direction={'row'} justify={SPACE_BETWEEN}>
            <Text type="lg" data-cy={'total-vat-excl'}>{`${t(
              'common.price.total_VAT_excl',
              'Total VAT. Excl',
            )}:`}</Text>
            <Text type="lg" data-cy={'total-vat-excl-number'}>
              {textFormatter.formatCurrency(totalPriceVatExcluded, currency)}
            </Text>
          </Flex>
          {totalDiscount > 0 && (
            <Flex direction={'row'} justify={SPACE_BETWEEN}>
              <Text type="lg" bold color="grey85" displayStyle={'error'}>
                {`${t('common.price.total_discount_VAT_excl', 'Total discount VAT. Excl')}:`}
              </Text>
              <Text type="lg" bold color="grey85" displayStyle={'error'}>
                {textFormatter.formatCurrency(totalDiscount, currency)}
              </Text>
            </Flex>
          )}
          <Flex direction={'row'} justify={SPACE_BETWEEN} data-cy={'vat'}>
            <Text type="lg">{t('common.price.vat', 'VAT:')}</Text>
            <Text type="lg" data-cy={'vat-number'}>
              {textFormatter.formatCurrency(totalVat, currency)}
            </Text>
          </Flex>
        </Flex>
        <Divider />
        <Flex direction={'column'} gap={9} padding={'17px 24px 24px 24px'}>
          <Flex direction={'row'} justify={SPACE_BETWEEN} data-cy={'total-vat-incl'}>
            <Text type="heading3" disableGutter displayStyle={'default'}>{`${t(
              'common.price.total_vat_incl',
              'Total VAT. Incl',
            )}:`}</Text>
            <Text type="heading3" disableGutter displayStyle={'default'} data-cy={'total-vat-incl-number'}>
              {textFormatter.formatCurrency(totalPriceVatIncluded, currency)}
            </Text>
          </Flex>
          <Flex direction={'row'} justify={'center'}>
            {loading ? (
              <CenteredSpin />
            ) : (
              <YellowButton
                onClick={handleCheckOut}
                disabled={disabledCheckout}
                data-cy={validationStep ? 'buy-button' : 'checkout-button'}
                shape={'semiRound'}
                size={'xlarge'}
                stretch
              >
                {!validationStep ? t('cart.action.checkout', 'Check-out') : t('cart.action.buy', 'Buy')}
              </YellowButton>
            )}
          </Flex>
        </Flex>
      </Card>
      <Text type="base" color="grey35">
        <Trans i18nKey={'cart.legal_notice'}>
          {'By placing your order, you agree to the Rpartstore '}
          <LinkText linkTo={ROUTER_TERMS_AND_CONDITIONS(country)}>{'Terms and Conditions'}</LinkText>
          {'. Please see our '}
          <LinkText linkTo={ROUTER_PERSONAL_DATA(country)}>{'Privacy Notice'}</LinkText>
          {' and '}
          <LinkText linkTo={ROUTER_COOKIES(country)}>{'Cookies'}</LinkText>.
        </Trans>
      </Text>
      <Box height={50} />
    </CheckoutCardWrapper>
  );
};

export default CheckoutCardSection;
